import { useCallback, type MouseEvent } from 'react';
import { useIntl } from 'react-intl';
import { Row } from 'react-flexbox-grid';
import { useHistory } from 'react-router-dom';
import useRemoveProject from 'hooks/project/useRemoveProject';
import { confirmAlert } from 'react-confirm-alert';
import { toast } from 'react-toastify';
import { useApolloClient } from '@apollo/client/react';
import useLastProjectId from 'hooks/project/useLastProjectId';
import { routePatterns } from 'router/route-paths';
import { useModal } from 'components/common/Modal';
import { LastProjectDocument, type ProjectsQuery } from 'graphql/generated/graphql';
import { messageOf } from 'utils/error';
import requireValue from 'utils/requireValue';
import CustomerInfoModal, { type ICustomerInfoModalData } from './CustomerInfoModal';
import ProjectCard from './ProjectCard';

interface IProjectListProps {
  projects: ProjectsQuery['projects'];
}

const useRemoveProjectHandler = () => {
  const removeProject = useRemoveProject();
  const { formatMessage: __ } = useIntl();

  const handleRemove = useCallback(
    async (projectId: string) => {
      try {
        confirmAlert({
          title: __({ id: 'my_projects.delete_project.title' }),
          message: __({ id: 'my_projects.delete_project.message' }),
          buttons: [
            {
              label: __({ id: 'my_projects.delete_project.cancel' }),
            },
            {
              label: __({ id: 'my_projects.delete_project.confirm' }),
              onClick: async () => {
                await removeProject(projectId);
              },
            },
          ],
        });
      } catch (error) {
        toast.error(messageOf(error));
      }
    },
    [__, removeProject],
  );
  return handleRemove;
};

const ProjectList = ({ projects }: IProjectListProps) => {
  const removeProject = useRemoveProjectHandler();
  const { lastProjectId } = useLastProjectId();
  const history = useHistory();
  const { showModal, hideModal, modalProps, isVisible } = useModal<ICustomerInfoModalData>();
  const client = useApolloClient();

  const changeLastProjectId = useCallback(
    (_id: string) => {
      // `__typename` is not selected, so the generated type lacks it; the cache needs it to normalise the project.
      const lastProject = { _id, __typename: 'Project' };
      client.writeQuery({ query: LastProjectDocument, data: { lastProject } });
    },
    [client],
  );

  const onTransferClick = useCallback(
    (projectId: string, name: string) => {
      showModal({ projectId, name });
    },
    [showModal],
  );

  const onCardClick = useCallback(
    (e: MouseEvent<HTMLDivElement>, _id: string) => {
      e.stopPropagation();
      changeLastProjectId(_id);
      history.push(routePatterns.dashboard.stringify({ id: _id }));
    },
    [changeLastProjectId, history],
  );

  return (
    <Row>
      {projects.map((projectOrNull) => {
        const project = requireValue(projectOrNull, '_id');
        return (
          <ProjectCard
            key={project._id}
            {...{
              isLastProject: projects.length <= 1,
              removeProject,
              onCardClick,
              onAdjustProject: changeLastProjectId,
              isActive: project._id === lastProjectId,
              onTransferClick,
              ...project,
            }}
          />
        );
      })}
      <CustomerInfoModal isVisible={isVisible} hideModal={hideModal} {...modalProps} />
    </Row>
  );
};

export default ProjectList;
